import React, { useState, useEffect, useRef } from "react";
import { IoCloseOutline } from "react-icons/io5";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import CustomField from "./common/customField";
import { TextField } from "@mui/material";
import BeatLoader from "react-spinners/BeatLoader";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getEmployeesData } from "../store/actions/action";

const AddTasks = ({ close, createTask, status, task }) => {
  const [taskIsLoading, setTaskIsLoading] = useState(false);
  const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { bussinessUuid } = useParams();
  const dispatch = useDispatch();
  const employees = useSelector((state) => state.employees.employees);
  const taskNameFromUrl = searchParams.get("taskName");
  const descriptionFromUrl = searchParams.get("description");
  const dueDateFromUrl = searchParams.get("dueDate");
  const priorityFromUrl = searchParams.get("priority");
  const assignedToFromUrl = searchParams.get("assignedTo");
  const autoSubmitFromUrl = searchParams.get("autoSubmit");
  const showDetailsFromUrl = searchParams.get("showDetails");
  const formikRef = useRef();

  const priorityOptions = [
    { value: "high", label: "High" },
    { value: "medium", label: "Medium" },
    { value: "low", label: "Low" },
  ];

  const tagOptions = [
    { value: "work", label: "Work" },
    { value: "personal", label: "Personal" },
    { value: "meeting", label: "Meeting" },
  ];
  
  const employeeOptions = [
    { value: "", label: t("FORM.SELECT_EMPLOYEE") },
    ...(employees?.map((emp) => ({
      value: emp._id,
      label: emp.employeeName || emp.fullName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.name || emp.email
    })) || [])
  ];

  let initialValues = {
    taskName: taskNameFromUrl || "",
    description: descriptionFromUrl || "",
    desiredTaskCompletionDate: dueDateFromUrl || "",
    priority: priorityFromUrl || "high",
    tag: "work",
    status: status || "6819e98b0025abf56b88363f",
    expectedStartDateTime: "",
    actualStartDateTime: "",
    actualEndDateTime: "",
    assignedTo: assignedToFromUrl || "",
    list: status || "",
  };

  if (task?._id) {
    initialValues = {
      ...initialValues,
      ...task,
      status: task.list || task.status || status,
      list: task.list || task.status || status,
      assignedTo: typeof task.assignedTo === 'object' ? task.assignedTo?._id : task.assignedTo,
    };
  }

  const taskSchema = Yup.object().shape({
    taskName: Yup.string().required("Task Name is required"),
    description: Yup.string().required("Description is required"),
    desiredTaskCompletionDate: Yup.date().required(
      "Completion Date is required"
    ),
    priority: Yup.string().required("Priority is required"),
    tag: Yup.string(),
    status: Yup.string(),
  });

  const submitForm = async (data) => {
    setTaskIsLoading(true);
    await createTask(data);
    setTaskIsLoading(false);

    // Clean up URL parameters after successful submission
    if (autoSubmitFromUrl === "true") {
      const cleanSearchParams = new URLSearchParams();
      cleanSearchParams.append("isOpen", "true");
      setSearchParams(cleanSearchParams);

      // Close the form after successful submission if it was auto-submitted
      setTimeout(() => {
        if (close) close();
      }, 1000);
    }
  };

  // Clean up URL parameters when component unmounts
  useEffect(() => {
    return () => {
      if (autoSubmitFromUrl === "true") {
        const cleanSearchParams = new URLSearchParams();
        cleanSearchParams.append("isOpen", "true");
        setSearchParams(cleanSearchParams);
      }
    };
  }, [autoSubmitFromUrl, setSearchParams]);

  useEffect(() => {
    if (bussinessUuid && (!employees || employees.length === 0)) {
      dispatch(getEmployeesData(bussinessUuid));
    }
  }, [bussinessUuid, dispatch, employees]);

  // Create a custom hook-like component for the auto-submit functionality
  const AutoSubmitTask = ({ formik, autoSubmit, showDetails }) => {
    useEffect(() => {
      if (
        autoSubmit === "true" &&
        formik &&
        !hasAutoSubmitted &&
        showDetails === "true"
      ) {
        // Show details for 3 seconds before submitting
        setShowCountdown(true);

        const countdownInterval = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        const submitTimer = setTimeout(() => {
          if (formik.isValid) {
            setHasAutoSubmitted(true);
            setShowCountdown(false);
            formik.submitForm();
          }
        }, 3000); // 3 seconds delay

        return () => {
          clearTimeout(submitTimer);
          clearInterval(countdownInterval);
        };
      }
    }, [autoSubmit, formik, hasAutoSubmitted, showDetails]);

    return null;
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={taskSchema}
      onSubmit={submitForm}
    >
      {(formik) => {
        formikRef.current = formik;

        const {
          values,
          isValid,
          dirty,
          handleChange,
          setFieldValue,
          handleBlur,
          errors,
          touched,
        } = formik;

        return (
          <Form>
            {/* Auto-submit component for delayed submission */}
            <AutoSubmitTask
              formik={formik}
              autoSubmit={autoSubmitFromUrl}
              showDetails={showDetailsFromUrl}
            />

            <div className='mx-auto max-w-3xl bg-gray-100 dark:bg-[#170f2a] py-4 px-0 rounded-lg'>
              <div className='border-2 border-gray-300 rounded-xl p-8 bg-white shadow-lg dark:bg-[#170f2a] dark:border-gray-300 dark:text-white transition-all duration-300 hover:shadow-xl'>
                <div className='space-y-3'>
                  <h2 className='text-lg font-semibold text-indigo-900 dark:text-white mb-2 text-left'>
                    {task ? t("FORM.UPDATE") : t("FORM.ADD")} {t("FORM.TASK")}
                  </h2>

                  {/* Show countdown if auto-submitting */}
                  {showCountdown && (
                    <div className='bg-blue-100 text-blue-800 p-3 rounded-lg mb-4 flex items-center justify-center'>
                      <div className='flex items-center'>
                        <BeatLoader color='#1e40af' size={8} className='mr-2' />
                        <span>
                          Creating your task in {countdown} seconds...
                        </span>
                      </div>
                      <button
                        type='button'
                        onClick={() => {
                          setShowCountdown(false);
                          setHasAutoSubmitted(true);
                          // Clean up URL parameters
                          const cleanSearchParams = new URLSearchParams();
                          cleanSearchParams.append("isOpen", "true");
                          setSearchParams(cleanSearchParams);
                        }}
                        className='ml-4 text-blue-600 hover:text-blue-800 text-sm font-medium'
                      >
                        Cancel Auto-Submit
                      </button>
                    </div>
                  )}

                  {/* Task Name */}
                  <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                    <div className='md:col-span-2'>
                      <CustomField
                        type='text'
                        name='taskName'
                        label={t("FORM.TASK_NAME")}
                        value={values.taskName}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        error={!!errors.taskName}
                        helperText={
                          errors.taskName ? t("FORM.TASK_ERROR_MESSAGE") : ""
                        }
                        FormHelperTextProps={{
                          className: "!text-red-500 dark:!text-red-500",
                        }}
                        sx={{
                          "& .MuiFormHelperText-root": {
                            color: errors.taskName ? "error.main" : undefined,
                          },
                        }}
                        disabled={showCountdown}
                      />
                    </div>

                    {/* Date Fields - Second Column */}
                    <div className='space-y-4'>
                      <TextField
                        type='datetime-local'
                        id='actualEndDateTime'
                        label={t("FORM.ACTUAL_END_DATE")}
                        name='actualEndDateTime'
                        className='w-full'
                        value={values.actualEndDateTime}
                        onChange={(e) => {
                          handleChange(e);
                          setTimeout(() => {
                            document
                              .getElementById("actualEndDateTime")
                              ?.blur();
                          }, 0);
                        }}
                        InputLabelProps={{
                          shrink: true,
                          className: "dark:text-white",
                        }}
                        InputProps={{
                          className:
                            "dark:text-white dark:bg-[#1f2937] dark:border-white dark:[color-scheme:dark] dark:[&::-webkit-calendar-picker-indicator]:filter-invert rounded-lg",
                        }}
                        size='small'
                        variant='outlined'
                        disabled={showCountdown}
                      />
                      <TextField
                        type='datetime-local'
                        id='desiredTaskCompletionDate'
                        label={t("FORM.TASK_COMPLETION_DATE")}
                        name='desiredTaskCompletionDate'
                        className='w-full'
                        value={values.desiredTaskCompletionDate}
                        onChange={(e) => {
                          handleChange(e);
                          setTimeout(() => {
                            document
                              .getElementById("desiredTaskCompletionDate")
                              ?.blur();
                          }, 0);
                        }}
                        InputLabelProps={{
                          shrink: true,
                          className: "dark:text-white",
                        }}
                        InputProps={{
                          className:
                            "dark:text-white dark:bg-[#1f2937] dark:border-white dark:[color-scheme:dark] dark:[&::-webkit-calendar-picker-indicator]:filter-invert rounded-lg",
                        }}
                        required
                        error={!!errors.desiredTaskCompletionDate}
                        helperText={
                          errors.desiredTaskCompletionDate
                            ? t("FORM.TASK_ERROR_MESSAGE")
                            : ""
                        }
                        size='small'
                        variant='outlined'
                        disabled={showCountdown}
                      />
                    </div>
                    {/* Date Fields - First Column */}
                    <div className='space-y-4'>
                      <TextField
                        type='datetime-local'
                        id='expectedStartDateTime'
                        label={t("FORM.TASK_START_DATE")}
                        name='expectedStartDateTime'
                        className='w-full'
                        value={values.expectedStartDateTime}
                        onChange={(e) => {
                          handleChange(e);
                          setTimeout(() => {
                            document
                              .getElementById("expectedStartDateTime")
                              ?.blur();
                          }, 0);
                        }}
                        InputLabelProps={{
                          shrink: true,
                          className: "dark:text-white",
                        }}
                        InputProps={{
                          className:
                            "dark:text-white dark:bg-[#1f2937] dark:border-white dark:[color-scheme:dark] dark:[&::-webkit-calendar-picker-indicator]:filter-invert rounded-lg",
                        }}
                        size='small'
                        variant='outlined'
                        disabled={showCountdown}
                      />

                      <TextField
                        type='datetime-local'
                        id='actualStartDateTime'
                        label={t("FORM.ACTUAL_START_DATE")}
                        name='actualStartDateTime'
                        className='w-full'
                        value={values.actualStartDateTime}
                        onChange={(e) => {
                          handleChange(e);
                          setTimeout(() => {
                            document
                              .getElementById("actualStartDateTime")
                              ?.blur();
                          }, 0);
                        }}
                        InputLabelProps={{
                          shrink: true,
                          className: "dark:text-white",
                        }}
                        InputProps={{
                          className:
                            "dark:text-white dark:bg-[#1f2937] dark:border-white dark:[color-scheme:dark] dark:[&::-webkit-calendar-picker-indicator]:filter-invert rounded-lg",
                        }}
                        size='small'
                        variant='outlined'
                        disabled={showCountdown}
                      />
                    </div>
                  </div>

                  {/* Priority, Tag, and Assigned To */}
                  <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                    {/* Priority Select */}
                    <div>
                      <CustomField
                        type='select'
                        name='priority'
                        label={t("FORM.PRIORITY")}
                        value={values.priority}
                        handleChange={handleChange}
                        options={priorityOptions}
                        setFieldValue={setFieldValue}
                        error={errors.priority}
                        touched={touched.priority}
                        helperText={errors.priority}
                        disabled={showCountdown}
                      />
                    </div>

                    {/* Tag Select */}
                    <div>
                      <CustomField
                        type='select'
                        name='tag'
                        label={t("FORM.TAG")}
                        value={values.tag}
                        handleChange={handleChange}
                        options={tagOptions}
                        setFieldValue={setFieldValue}
                        error={errors.tag}
                        touched={touched.tag}
                        helperText={errors.tag}
                        disabled={showCountdown}
                      />
                    </div>

                    {/* Assigned To Dropdown */}
                    <div className='md:col-span-1'>
                      <CustomField
                        type='select'
                        name='assignedTo'
                        label={t("FORM.ASSIGNED_TO")}
                        value={values.assignedTo}
                        handleChange={handleChange}
                        options={employeeOptions}
                        setFieldValue={setFieldValue}
                        disabled={showCountdown}
                        placeholder={t("FORM.SELECT_EMPLOYEE")}
                        error={errors.assignedTo}
                        touched={touched.assignedTo}
                        helperText={errors.assignedTo}
                      />
                    </div>
                  </div>
                  {/* Description */}
                  <div className='md:col-span-2'>
                    <TextField
                      id='description'
                      name='description'
                      label={t("FORM.DESCRIPTION")}
                      placeholder={t("FORM.DESCRIPTION")}
                      onChange={handleChange}
                      multiline
                      rows={2}
                      value={values.description}
                      className='w-full'
                      error={!!errors.description}
                      required
                      helperText={
                        errors.description ? t("FORM.TASK_ERROR_MESSAGE") : ""
                      }
                      InputProps={{
                        className:
                          "dark:text-white dark:bg-[#1f2937] dark:border-white rounded-lg",
                      }}
                      InputLabelProps={{
                        className: "dark:text-white",
                      }}
                      variant='outlined'
                      disabled={showCountdown}
                    />
                  </div>
                  {/* Assigned To */}

                  {/* Submit Button - Show only if not auto-submitting or if user canceled auto-submit */}
                  {(!showCountdown || hasAutoSubmitted) && (
                    <div className='mt-4 '>
                      <button
                        type='submit'
                        disabled={task ? (!isValid || taskIsLoading) : (!(dirty && isValid) || taskIsLoading)}
                        className={`w-full py-2 px-6 rounded-xl font-bold text-white transition-all duration-300 ${(task ? !isValid : !(dirty && isValid)) || taskIsLoading
                          ? "bg-gray-400 dark:bg-gray-600 cursor-not-allowed"
                          : " bg-blue-600  hover:from-blue-700 shadow-md hover:shadow-lg"
                          } flex items-center justify-center space-x-2`}
                      >
                        <span>
                          {task ? t("FORM.UPDATE") : t("FORM.ADD")}{" "}
                          {t("FORM.TASK")}
                        </span>
                        {taskIsLoading && (
                          <BeatLoader color='#ffffff' size={10} />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AddTasks;
